import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text, Flex } from 'components/ui';
import ProductImage from 'components/Product/ProductImage';
import CompareZoneBase from 'components/Catalog/ProductGrid/CompareModal/Zones/CompareZoneBase';
import { useComparisonSkus, processComparisonValue, EmptyState } from 'components/Catalog/ProductGrid/hooks/useComparisonSkus';
import skuUtils from 'utils/Sku';
import { radii, fontSizes, lineHeights } from 'style/config';

const ColorsZone = ({ comparisonData = {}, getText = () => {} }) => {
    const skus = useComparisonSkus(comparisonData);

    const renderContent = (sku, _index) => {
        const valueData = processComparisonValue(
            sku,
            skuItem => skuItem.color,
            (validatedSku, parentProduct, uniqueColors) => {
                // Check if this is a color variation product
                const variationType = parentProduct.variationType?.toLowerCase();
                const isColorProduct = variationType === skuUtils.skuVariationType.COLOR.toLowerCase();
                const hasColorValue = !!validatedSku.color;

                return (isColorProduct || hasColorValue) && uniqueColors.size > 0;
            }
        );

        if (!valueData) {
            return <EmptyState />;
        }

        const { currentValue: colorName, additionalValues: additionalColors, validatedSku } = valueData;
        const colorImage = validatedSku?.primaryImages?.[0]?.baseUrl;

        return (
            <Flex
                flexDirection='column'
                gap={1}
            >
                <Flex
                    alignItems='center'
                    gap={2}
                >
                    {colorImage && (
                        <Box css={styles.colorThumbnail}>
                            <ProductImage
                                src={colorImage}
                                altText={colorName || ''}
                                size={24}
                                disableLazyLoad={true}
                                objectFit='cover'
                            />
                        </Box>
                    )}
                    <Text
                        fontSize='sm'
                        css={styles.colorName}
                        numberOfLines={2}
                        is='p'
                    >
                        {colorName ? `${getText('color')}: ${colorName}` : ''}
                    </Text>
                </Flex>
                {additionalColors > 0 && (
                    <Text
                        fontSize='sm'
                        color='gray'
                        is='p'
                    >
                        + {additionalColors} {additionalColors === 1 ? getText('color').toLowerCase() : getText('colors').toLowerCase()}
                    </Text>
                )}
            </Flex>
        );
    };

    return (
        <CompareZoneBase
            selectedProducts={skus}
            getText={getText}
            titleKey='colors'
            renderContent={renderContent}
        />
    );
};

const styles = {
    colorThumbnail: {
        width: '24px',
        height: '24px',
        borderRadius: `${radii[999]}px`,
        flexShrink: 0,
        overflow: 'hidden'
    },
    colorName: {
        flex: '1 0 0',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        lineHeight: lineHeights.sm,
        fontSize: `${fontSizes.sm}px`
    }
};

ColorsZone.propTypes = {
    comparisonData: PropTypes.object,
    getText: PropTypes.func
};

export default wrapFunctionalComponent(ColorsZone, 'ColorsZone');
